import {
  ActivitySpinnerSize, ActivitySpinnerWidget,
  ListItem, LocallyFilteringList,
  LocallyFilteringListProps
} from '@tableau/widgets';
import * as React from 'react';
import * as tabbable from 'tabbable';

import { Messages } from '../../codegen/Localize';
import { ServerUrls } from '../ServerUrls';
import { NavStyles } from './NavigationStyles';
import { SITE_PICKER_CHECK } from './SitePicker';
import { SitePickerStyles } from './SitePickerStyles';

export interface SiteFilteringListProps extends LocallyFilteringListProps, Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  readonly requestSites: () => void;
  readonly isSessionServerAdmin: boolean;
  readonly serverUrls: ServerUrls;
  readonly inServerArea: boolean;

  readonly items: ListItem[];
  readonly onManageAllSitesLinkClick: () => void;

  readonly id: string;
}

export class SiteFilteringList extends React.Component<SiteFilteringListProps, {}> {
  private containerElement: HTMLElement = null;

  public componentDidMount(): void {
    this.props.requestSites();
  }

  public render(): JSX.Element {
    const { requestSites, isSessionServerAdmin, serverUrls, inServerArea, onManageAllSitesLinkClick: onLinkClick,
      ...listProps } = this.props;

    if (this.props.items.length <= 0) {
      // show spinner until we have the items list
      return (
        <div className={SitePickerStyles.Spinner} >
          <ActivitySpinnerWidget
            spinnerSize={ActivitySpinnerSize.Small}
            testId="site-list-spinner" />
        </div>);
    }

    // We don't make "Manage All Sites" a list item because we want the site-list
    // to be scrollable but the All Sites link to be sticky at the bottom of the panel
    /* eslint-disable jsx-a11y/no-autofocus */
    /* eslint-disable jsx-a11y/no-static-element-interactions */
    return (
      <div
        className={SitePickerStyles.Panel}
        data-tb-test-id="site-switcher-panel"
        ref={this.handleRef}
        onKeyDown={this.handleKeyDown}
        id={this.props.id}
      >
        <LocallyFilteringList {...listProps} autoFocus={true} style={SitePickerStyles.List} />
        {isSessionServerAdmin && this.renderManageAllSitesLink()}
      </div>
    );
  }

  private renderManageAllSitesLink = (): JSX.Element => {
    /* eslint-disable jsx-a11y/alt-text */
    return (
      <div data-tb-test-id="site-picker-server-admin-section">
        <div className={NavStyles.SeparatorSitePicker} />
        {this.props.inServerArea && <img src={SITE_PICKER_CHECK} className={SitePickerStyles.CheckImgForAllSitesItem} />}
        <a className={this.props.inServerArea ? SitePickerStyles.ManageAllSitesLinkSelected : SitePickerStyles.ManageAllSitesLink}
          onClick={this.props.onManageAllSitesLinkClick}
          data-tb-test-id="manage-all-sites-link"
          href={this.props.serverUrls.sitesUrl()}>
          {Messages.siteSwitcher_manageAllSites()}
        </a>
      </div>
    );
  }

  private handleRef = (ref: HTMLElement | null): void => {
    this.containerElement = ref;
  }

  private handleKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    if (e.key !== 'Tab') return;

    // Setting a focus trap to keep focus on the site picker flyout panel
    const tabbables = tabbable(this.containerElement);
    const first = tabbables.length > 0 ? tabbables[0] : null;
    const last = tabbables.length > 0 ? tabbables[tabbables.length - 1] : null;
    if (e.target === last && !e.shiftKey) {
      e.preventDefault();
      first.focus();
    } else if (e.target === first && e.shiftKey) {
      e.preventDefault();
      last.focus();
    }
  }
}
